import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  TextField,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  CircularProgress,
  Box,
  Typography,
  ClickAwayListener,
  Popper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import StadiumIcon from '@mui/icons-material/Stadium';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, SEARCH_DEBOUNCE_MS, MIN_SEARCH_LENGTH } from './searchConfig';
import useIsMobile from '../../hooks/useIsMobile';

export const normalizeSearchText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export const findExactSearchMatch = (query, suggestions = []) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return null;

  return suggestions.find((item) => {
    const candidates = [item?.name, item?.display_name, item?.details_name]
      .map(normalizeSearchText)
      .filter(Boolean);
    return candidates.includes(normalizedQuery);
  }) || null;
};

const SearchBar = ({
  onSelect,
  onSubmit,
  onFallback,
  placeholder = "Search players, teams, or venues...",
  autoFocus = false,
  // The app is dark throughout, so dark is the default; 'light' is kept for any light surface.
  variant = 'dark',
  // The header's slide-down search sits inside the 52px top bar: a 40px field instead of 56px.
  dense = false,
}) => {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const anchorRef = useRef(null);
  const dark = variant === 'dark';

  const fetchSuggestions = async (queryText, limit = 10) => {
    const q = queryText.trim();
    if (q.length < MIN_SEARCH_LENGTH) {
      return [];
    }

    const response = await axios.get(`${API_BASE_URL}/search/suggestions`, {
      params: { q, limit }
    });
    return response.data.suggestions || [];
  };

  const getIcon = (type) => {
    switch (type) {
      case 'player': return <PersonIcon color="primary" />;
      case 'team': return <GroupsIcon color="success" />;
      case 'venue': return <StadiumIcon color="secondary" />;
      default: return <SearchIcon />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'player': return 'Player';
      case 'team': return 'Team';
      case 'venue': return 'Venue';
      default: return '';
    }
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const nextSuggestions = await fetchSuggestions(query, 10);
        setSuggestions(nextSuggestions);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Search error:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleSelect = (item) => {
    // Use display_name for the input field if available
    setQuery(item.display_name || item.name);
    setShowSuggestions(false);
    if (onSelect) {
      // Pass the full item with both name (legacy) and display_name
      onSelect(item);
    }
  };

  const routeToQueryBuilder = (queryText) => {
    const q = queryText.trim();
    if (!q) return;

    if (onFallback) {
      onFallback(q);
      return;
    }

    navigate(`/query?nl=${encodeURIComponent(q)}`);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    const q = query.trim();
    if (!q || submitting) return;

    setSubmitting(true);
    try {
      const freshSuggestions = await fetchSuggestions(q, 20);
      setSuggestions(freshSuggestions);
      const exactMatch = findExactSearchMatch(q, freshSuggestions);

      if (onSubmit) {
        onSubmit({ query: q, exactMatch, suggestions: freshSuggestions });
      }

      if (exactMatch) {
        handleSelect(exactMatch);
        return;
      }
      // Not an exact name: ask the API whether this is really one player/team/venue page
      // ("abhishek sharma batting", "wankhede") before handing it to the query builder.
      let routed = null;
      try {
        const { data } = await axios.get(`${API_BASE_URL}/search/route`, { params: { q }, timeout: 4000 });
        routed = data?.route === 'entity' ? data.item : null;
      } catch {
        routed = null;
      }
      setShowSuggestions(false);
      if (routed) {
        handleSelect(routed);
      } else {
        routeToQueryBuilder(q);
      }
    } catch (error) {
      console.error('Search submit error:', error);
      setShowSuggestions(false);
      routeToQueryBuilder(q);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClickAway = () => {
    setShowSuggestions(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        component="form"
        ref={anchorRef}
        onSubmit={handleSubmit}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 680,
          zIndex: 1300,
          display: 'flex',
          gap: 1,
          alignItems: 'stretch'
        }}
      >
        <TextField
          fullWidth
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={isMobile ? 'Players, teams, venues' : placeholder}
          size={dense ? 'small' : 'medium'}
          variant="outlined"
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          InputProps={{
            // Phones show the (tappable) search icon at the end instead; two magnifiers read as a bug.
            startAdornment: !isMobile && (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: dark ? '#9aa1ac' : 'action.active' }} />
              </InputAdornment>
            ),
            // Phones: the submit sits inside the field (the separate square button squeezed the
            // field and cut the placeholder off at 390px).
            endAdornment: (loading || isMobile) && (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton
                    type="submit"
                    edge="end"
                    aria-label="Search"
                    disabled={!query.trim() || submitting}
                    sx={{ width: dense ? 36 : 40, height: dense ? 36 : 40, color: dark ? '#b6f24a' : 'primary.main' }}
                  >
                    <SearchIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
            sx: {
              borderRadius: 3,
              bgcolor: dark ? '#101319' : 'white',
              color: dark ? '#f3f4f6' : undefined,
              '& input': { color: dark ? '#f3f4f6' : undefined },
              '& input::placeholder': {
                color: dark ? '#6b7280' : undefined,
                opacity: 1,
              },
              '& fieldset': { borderColor: dark ? 'rgba(255,255,255,0.14)' : '#dfe1e5' },
              '&:hover fieldset': { borderColor: dark ? 'rgba(182,242,74,0.42)' : '#c0c0c0' },
              '&.Mui-focused fieldset': { borderColor: dark ? '#b6f24a' : undefined },
            }
          }}
        />
        <Box
          component="button"
          type="submit"
          disabled={!query.trim() || submitting}
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            alignItems: 'center',
            justifyContent: 'center',
            border: 0,
            px: { xs: 1.8, sm: 2.5 },
            borderRadius: 3,
            bgcolor: dark ? '#b6f24a' : '#1976d2',
            color: dark ? '#0a0c11' : 'white',
            fontWeight: 700,
            cursor: query.trim() && !submitting ? 'pointer' : 'default',
            opacity: query.trim() && !submitting ? 1 : 0.55,
            minWidth: { xs: 50, sm: 94 },
            fontFamily: 'inherit',
            '&:hover': {
              bgcolor: query.trim() && !submitting
                ? (dark ? '#c8f56f' : '#1565c0')
                : (dark ? '#b6f24a' : '#1976d2')
            }
          }}
          aria-label="Search"
        >
          {submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Search
              </Box>
              <SearchIcon sx={{ display: { xs: 'inline-flex', sm: 'none' }, verticalAlign: 'middle' }} />
            </>
          )}
        </Box>
        
        <Popper
          open={showSuggestions && suggestions.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{
            zIndex: 1700,
            width: anchorRef.current?.getBoundingClientRect().width || undefined,
            maxWidth: 'calc(100vw - 24px)',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              mt: 0.5,
              maxHeight: 400,
              overflow: 'auto',
              borderRadius: 2,
              bgcolor: dark ? '#161a22' : 'background.paper',
              color: dark ? '#f3f4f6' : undefined,
              border: dark ? '1px solid rgba(255,255,255,0.12)' : undefined,
              boxShadow: dark ? '0 24px 48px -18px rgba(0,0,0,0.75)' : undefined,
            }}
          >
            <List dense>
              {suggestions.map((item, index) => (
                <ListItemButton
                  key={`${item.type}-${item.name}-${index}`}
                  onClick={() => handleSelect(item)}
                  sx={{
                    '&:hover': { bgcolor: dark ? 'rgba(182,242,74,0.1)' : 'action.hover' },
                    py: 1
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getIcon(item.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.display_name || item.name}
                    primaryTypographyProps={{ color: dark ? '#f3f4f6' : undefined }}
                    secondary={
                      <Typography variant="caption" sx={{ color: dark ? '#9aa1ac' : 'text.secondary' }}>
                        {getTypeLabel(item.type)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchBar;
